import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDragPlaceholder,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostListener,
  Injector,
  afterNextRender,
  computed,
  effect,
  inject,
  model,
  signal,
  untracked,
} from '@angular/core';
import { Checkbox } from '@interfaces/a-organizar/checkbox';
import { ConfirmationService } from '@src/app/core/services/helpers/confirmation.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { SharedModule } from '@src/app/shared/shared.module';
import { describeClientError } from '@shared/utils/client-error-message';
import { DrawerModule } from 'primeng/drawer';
import {
  HOME_LAYOUT_SAVE_ERROR,
  HomeLayoutService,
} from '@features/dashboard/dashboard-management/services/home-layout.service';
import {
  HomeBlockId,
  HomeLayoutItem,
  homeBlockDefinition,
} from '@features/dashboard/dashboard-management/utils/home-blocks';
import { sameHomeLayout } from '@features/dashboard/dashboard-management/utils/resolve-home-layout';

/** Toast de sucesso ao salvar. */
export const HOME_CUSTOMIZED_MESSAGE = 'Home personalizada.';

/** Classe no `body` com a gaveta aberta: esconde os FABs do shell (10 400), que cobririam o Salvar (gaveta em 10 300). */
const DRAWER_OPEN_BODY_CLASS = 'home-customize-open';

/** Linha da gaveta: o bloco com o rótulo e o checkbox do `app-checkbox`. */
interface CustomizeRow {
  id: HomeBlockId;
  label: string;
  checkbox: Checkbox;
  /** Dica quando o bloco está ligado mas não aparece com a empresa escolhida agora (ex.: Score sem empresa única). */
  hint: string | null;
}

/**
 * Gaveta "Personalizar Dashboard" da Home (card 3788, protótipo `nova-dashboard-com-cog-e-limites`, tela
 * `personalizar`). A pessoa arrasta os blocos para mudar a ordem (CDK, como a tabela de cotações do Raio X; com foco na
 * alça, setas para cima e para baixo movem) e desliga o que não quer ver. Nada muda na Home até **Salvar**;
 * **Restaurar Padrão** só volta a lista da gaveta para a ordem padrão com tudo ligado.
 *
 * `p-drawer` do PrimeNG direto: o Design System Vault não tem wrapper de painel lateral (os filtros do projeto usam o
 * mesmo `p-drawer`). Z-index fixo 10 300 (faixa das modais, `src/styles/Z-INDEX.md`, `autoZIndex` desligado): o
 * automático do PrimeNG somava o contador de overlays e passava do toast (10 500), que ficava escondido atrás. Com a
 * gaveta aberta os FABs do shell somem (classe `home-customize-open` no `body`). Fechar com mudança não salva pede
 * confirmação; por isso o fechar (X, Esc) é da gaveta e não do PrimeNG.
 */
@Component({
  selector: 'app-home-customize-drawer',
  standalone: true,
  imports: [SharedModule, DrawerModule, CdkDropList, CdkDrag, CdkDragHandle, CdkDragPlaceholder],
  templateUrl: './home-customize-drawer.component.html',
  styleUrl: './home-customize-drawer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeCustomizeDrawerComponent {
  /** Gaveta aberta. */
  readonly visible = model(false);

  private readonly layout = inject(HomeLayoutService);
  private readonly toast = inject(ToastHelperService);
  private readonly document = inject(DOCUMENT);
  private readonly injector = inject(Injector);
  private readonly confirmation = inject(ConfirmationService);
  /** Confirmação de descarte aberta (o Esc dela não abre outra). */
  private confirmingClose = false;

  /** Blocos na ordem da gaveta (ainda não salvos). */
  protected readonly rows = signal<CustomizeRow[]>([]);
  /** Anúncio para leitor de tela depois de mover pelo teclado. */
  protected readonly liveMessage = signal('');
  protected readonly saving = this.layout.saving;
  /** Erro do último Salvar, mostrado dentro da gaveta acima do rodapé (some ao mexer na lista ou reabrir). */
  protected readonly saveError = signal<string | null>(null);

  /** A gaveta tem mudança que ainda não foi salva (Salvar só habilita com mudança). */
  protected readonly dirty = computed(
    () => !sameHomeLayout(this.toItems(this.rows()), this.layout.layout())
  );

  /** Texto do topo: na conta vale em qualquer computador; sem a permissão, só neste navegador. */
  protected readonly intro = computed(() =>
    this.layout.source() === 'account'
      ? 'Arraste para mudar a ordem e desligue o que não quer ver. Vale só para você, em qualquer computador.'
      : 'Arraste para mudar a ordem e desligue o que não quer ver. Vale só para você, neste navegador.'
  );

  constructor() {
    // A cada abertura, a gaveta começa pela arrumação que está na Home (descarta o que não foi salvo).
    effect(() => {
      const open = this.visible();
      this.document.body.classList.toggle(DRAWER_OPEN_BODY_CLASS, open);
      if (open) {
        untracked(() => {
          this.saveError.set(null);
          this.rows.set(this.toRows(this.layout.layout()));
        });
      }
    });
    // A dica de "aparece com empresa escolhida" acompanha o seletor de empresa mesmo com a gaveta aberta.
    effect(() => {
      const hints = this.layout.selectionHints();
      untracked(() =>
        this.rows.update(rows => rows.map(row => ({ ...row, hint: hints[row.id] ?? null })))
      );
    });
    inject(DestroyRef).onDestroy(() => this.document.body.classList.remove(DRAWER_OPEN_BODY_CLASS));
  }

  /** Esc fecha (com confirmação se houver mudança), como o X do cabeçalho. */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.visible() && !this.confirmingClose) {
      this.requestClose();
    }
  }

  /**
   * Soltou uma linha: muda a posição na gaveta.
   * @param event Evento do CDK.
   */
  drop(event: CdkDragDrop<CustomizeRow[]>): void {
    this.move(event.previousIndex, event.currentIndex);
  }

  /**
   * Teclas da alça: setas para cima e para baixo movem o bloco uma posição e devolvem o foco à alça.
   * @param event Tecla.
   * @param index Posição atual do bloco.
   */
  gripKeydown(event: KeyboardEvent, index: number): void {
    const delta = event.key === 'ArrowUp' ? -1 : event.key === 'ArrowDown' ? 1 : 0;
    if (!delta) {
      return;
    }
    event.preventDefault();
    const row = this.rows()[index];
    if (!this.move(index, index + delta) || !row) {
      return;
    }
    const position = index + delta + 1;
    this.liveMessage.set(`${row.label}: posição ${position} de ${this.rows().length}.`);
    afterNextRender(
      () => this.document.querySelector<HTMLElement>(`[data-home-grip-id="${row.id}"]`)?.focus(),
      { injector: this.injector }
    );
  }

  /**
   * Liga ou desliga um bloco na gaveta.
   * @param id Bloco.
   * @param checkbox Checkbox devolvido pelo `app-checkbox`.
   */
  toggle(id: HomeBlockId, checkbox: Checkbox): void {
    const visible = !!checkbox.isChecked;
    this.rows.update(rows =>
      rows.map(row =>
        row.id === id ? { ...row, checkbox: this.buildCheckbox(id, row.label, visible) } : row
      )
    );
    this.saveError.set(null);
  }

  /** Volta a lista da gaveta para a ordem padrão com tudo ligado (só grava ao Salvar). */
  restoreDefault(): void {
    this.rows.set(this.toRows(this.layout.defaultLayout()));
    this.saveError.set(null);
  }

  /**
   * Grava, aplica na Home, fecha e avisa. Erro: a mensagem do backend aparece dentro da gaveta (acima do rodapé), sem
   * toast repetindo a frase, e a gaveta continua aberta.
   */
  save(): void {
    if (this.saving() || !this.dirty()) {
      return;
    }
    this.saveError.set(null);
    this.layout.save(this.toItems(this.rows())).subscribe({
      next: () => {
        this.toast.showToastSuccess(HOME_CUSTOMIZED_MESSAGE);
        this.visible.set(false);
      },
      error: (error: unknown) => {
        const message = describeClientError(error, HOME_LAYOUT_SAVE_ERROR);
        // Só o aviso dentro da gaveta: o toast repetia a mesma frase por cima do X (crítica F4, N1).
        this.saveError.set(message);
      },
    });
  }

  /** X do cabeçalho e Esc: sem mudança fecha; com mudança não salva, pergunta antes de descartar. */
  requestClose(): void {
    if (!this.dirty() || this.saving()) {
      if (!this.saving()) {
        this.visible.set(false);
      }
      return;
    }
    this.confirmingClose = true;
    this.confirmation
      .confirm(
        'Descartar as mudanças na Home?',
        'A ordem e os blocos que você mudou nesta gaveta ainda não foram salvos.',
        'danger',
        'Continuar editando',
        'Descartar'
      )
      .subscribe(discard => {
        this.confirmingClose = false;
        if (discard) {
          this.visible.set(false);
        }
      });
  }

  /**
   * Move um bloco na gaveta.
   * @param from Posição atual.
   * @param to Posição nova.
   * @returns `true` quando mudou alguma coisa.
   */
  private move(from: number, to: number): boolean {
    const rows = [...this.rows()];
    if (from === to || from < 0 || to < 0 || from >= rows.length || to >= rows.length) {
      return false;
    }
    moveItemInArray(rows, from, to);
    this.rows.set(rows);
    this.saveError.set(null);
    return true;
  }

  private toRows(items: readonly HomeLayoutItem[]): CustomizeRow[] {
    const hints = this.layout.selectionHints();
    return items.map(item => {
      const label = homeBlockDefinition(item.id).label;
      return {
        id: item.id,
        label,
        checkbox: this.buildCheckbox(item.id, label, item.visible),
        hint: hints[item.id] ?? null,
      };
    });
  }

  private toItems(rows: readonly CustomizeRow[]): HomeLayoutItem[] {
    return rows.map(row => ({ id: row.id, visible: !!row.checkbox.isChecked }));
  }

  private buildCheckbox(id: HomeBlockId, label: string, checked: boolean): Checkbox {
    return {
      id,
      label,
      name: `home-block-${id}`,
      value: id,
      isChecked: checked,
      isDisabled: false,
    };
  }
}
